import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import {
  LEGACY_TENANT,
  MOCK_MEMBERSHIP,
  MOCK_OFF,
  PENDING_MEMBERSHIP,
  SPACE_META,
  isMockMode,
  membershipFrom,
  PREVIEW_TOOLS,
  isSpaceKey,
  isTenantKey,
  parseSpacePath,
  spaceHomePath,
  spacesFor,
  swapBizInPath,
  swapTenantInPath,
  type MockMode,
  type SpaceKey,
  type SpaceMembership,
  type TenantKey,
  type BizKey,
} from "@/domain/space";
import { findMenu, isHomeKey, menuKeyOfPath, menuPath, spaceHasMenu, type MenuDef } from "@/domain/menus";
import type { Permission } from "@/domain/user";
import { isEmployedMember, isMemberActive } from "@/domain/membership";
import { membersRepo, type MyMembershipSnapshot } from "@/services/members.repository";
import { SpaceContext, type SpaceContextValue, type SwitchSpaceOptions } from "./space-context";

/**
 * SpaceProvider — 공간(내 업무 / 중앙 / 경원) 상태. B′ 미리보기.
 *
 * - 공간의 정본은 URL(2026-10-20 1단계 → 10-22 D-26): `/my…` = 내 업무, `/c/{tenant}/{biz}/…`·`/c/{tenant}/…` = 법인.
 *   새로고침·링크 공유 시 같은 법인이 열린다. localStorage 는 "마지막 공간"(기본 공간 판정)만 기억.
 * - 소속 아닌/없는 법인 주소는 TenantGate(router/TenantRoutes.tsx)가 기본 공간 홈으로 돌린다.
 *   여기서는 그런 주소를 공간으로 인정하지 않을 뿐(헤더·사이드바가 남의 법인 색으로 한 프레임도 안 바뀌게).
 * - 소속(2026-10-23 D 묶음): 내 소속 문서 `tenants/{t}/members/{uid}` 2건을 구독해 열 수 있는 법인·법인 관리자 판정
 *   (domain/space.ts membershipFrom). 전체 admin 은 두 법인 모두. 소속 문서가 하나도 없는 옛 계정은 중앙만(운영 무중단).
 *   전체 admin 이 아니면 소속을 확인할 때까지 화면을 그리지 않는다(남의 법인 화면이 한 프레임도 안 보이게, TenantGate 오판 방지).
 * - 가상 소속(미리보기): 개발 서버에서 `?mock=both|jadmin|kadmin|joongang|driver` 를 주면 그 소속으로 본다(localStorage 기억),
 *   `?mock=off` 로 실제 소속. 운영 빌드는 늘 실제 소속·사이드바 색조 켬.
 * - Router 안(Layout 바깥)에서만 쓴다 — useNavigate 필요.
 */
const LS_SPACE = "rmc.space.last";
const LS_TENANT = "rmc.space.lastTenant";
const LS_MOCK = "rmc.preview.mock";
const LS_TINT = "rmc.preview.sidebarTint";

function readLS(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeLS(key: string, v: string) {
  try { localStorage.setItem(key, v); } catch { /* private mode 등 — 기억만 못 함 */ }
}

export function SpaceProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPerm, user } = useAuth();
  const path = location.pathname;

  const uid = user?.uid ?? null;
  const isAdmin = !!user?.isAdmin;

  // ── 가상 소속(개발 서버 미리보기 — ?mock= 을 줄 때만) ──
  const queryMock = new URLSearchParams(location.search).get("mock");
  const [storedMock, setStoredMock] = useState<MockMode | null>(() => {
    if (isMockMode(queryMock)) return queryMock;
    if (queryMock === MOCK_OFF) return null;
    const ls = readLS(LS_MOCK);
    return isMockMode(ls) ? ls : null;
  });
  // 쿼리가 바뀌면 렌더 중 보정(React 권장 "이전 값에서 파생" 패턴 — effect setState 금지 규칙 회피)
  if (isMockMode(queryMock) && queryMock !== storedMock) setStoredMock(queryMock);
  if (queryMock === MOCK_OFF && storedMock !== null) setStoredMock(null);
  const mockMode: MockMode | null = PREVIEW_TOOLS ? storedMock : null;
  useEffect(() => {
    if (!PREVIEW_TOOLS) return;
    try {
      if (mockMode) localStorage.setItem(LS_MOCK, mockMode);
      else localStorage.removeItem(LS_MOCK);
    } catch { /* private mode 등 */ }
  }, [mockMode]);

  // ── 실제 소속 — 내 소속 문서 구독(uid 별로 상태를 담아 계정 전환 시 이전 값이 섞이지 않게) ──
  const [mine, setMine] = useState<{ uid: string; snap: MyMembershipSnapshot } | null>(null);
  useEffect(() => {
    if (!uid) return;
    return membersRepo.watchMine(uid, (snap) => setMine({ uid, snap }));
  }, [uid]);
  const mineSnap = mine && mine.uid === uid ? mine.snap : null;
  const membershipLoaded = mockMode !== null || isAdmin || mineSnap !== null;

  // ── 사이드바 색조 실험 (기본 켬) ──
  const [sidebarTint, setTintState] = useState<boolean>(() => !PREVIEW_TOOLS || readLS(LS_TINT) !== "0");
  const setSidebarTint = useCallback((on: boolean) => {
    setTintState(on);
    writeLS(LS_TINT, on ? "1" : "0");
  }, []);

  const membership: SpaceMembership = useMemo(() => {
    if (mockMode) return MOCK_MEMBERSHIP[mockMode];
    if (!isAdmin && !mineSnap) return PENDING_MEMBERSHIP;
    const rows = (mineSnap?.rows ?? []).map((m) => ({
      tenant: m.tenant,
      active: isMemberActive(m),
      tenantAdmin: m.tenantAdmin,
      employed: isEmployedMember(m),
    }));
    return membershipFrom(rows, isAdmin, mineSnap?.anyDoc ?? false);
  }, [mockMode, isAdmin, mineSnap]);
  const tenants = membership.tenants;
  const spaces = useMemo(() => spacesFor(membership), [membership]);
  const myBizs = useMemo(() => {
    const out: Partial<Record<TenantKey, BizKey[]>> = {};
    for (const m of mineSnap?.rows ?? []) if (isMemberActive(m) && m.bizs.length) out[m.tenant] = m.bizs;
    return out;
  }, [mineSnap]);

  // ── URL → 공간 (소속 아닌 법인 주소는 공간으로 인정하지 않음) ──
  const parsed = parseSpacePath(path);
  const urlSpace: SpaceKey | null =
    parsed.space === "my" || (parsed.space !== null && tenants.includes(parsed.space)) ? parsed.space : null;

  // 마지막 공간 — URL 공간이 바뀔 때마다 렌더 중 갱신(값이 다를 때만 set → 루프 없음)
  const [lastSpace, setLastSpace] = useState<SpaceKey | null>(() => {
    const v = readLS(LS_SPACE);
    return isSpaceKey(v) ? v : null;
  });
  const [lastTenant, setLastTenant] = useState<TenantKey | null>(() => {
    const v = readLS(LS_TENANT);
    return isTenantKey(v) ? v : null;
  });
  if (urlSpace !== null && urlSpace !== lastSpace) setLastSpace(urlSpace);
  if (urlSpace !== null && isTenantKey(urlSpace) && urlSpace !== lastTenant) setLastTenant(urlSpace);

  const fallbackTenant: TenantKey | null =
    lastTenant && tenants.includes(lastTenant) ? lastTenant : (tenants[0] ?? null);
  /** 기본 공간 = 마지막 공간(열 수 있으면) → 마지막/첫 근무 법인 → 내 업무. D-04(로그인 첫 화면)는 2단계 */
  const defaultSpace: SpaceKey =
    lastSpace && spaces.includes(lastSpace) ? lastSpace : (fallbackTenant ?? "my");
  const space: SpaceKey = urlSpace ?? defaultSpace;
  const tenant: TenantKey | null = isTenantKey(space) ? space : null;
  const dataTenant: TenantKey = tenant ?? LEGACY_TENANT;
  const biz: BizKey | null = urlSpace !== null && urlSpace === parsed.space ? parsed.biz : null;

  // 기억
  useEffect(() => {
    if (urlSpace === null) return;
    writeLS(LS_SPACE, urlSpace);
    if (isTenantKey(urlSpace)) writeLS(LS_TENANT, urlSpace);
  }, [urlSpace]);

  // ── 법인 단위 권한 (1단계: 소속 + 계정 전체 권한. 2단계: 법인별 역할) ──
  const hasPermIn = useCallback(
    (key: Permission, t?: TenantKey) => {
      const target = t ?? tenant;
      if (target && !tenants.includes(target)) return false;
      return hasPerm(key, target ?? undefined);
    },
    [tenant, tenants, hasPerm],
  );
  const adminOf = membership.adminOf;
  const groupAdmin = isAdmin && membership.groupAdmin;
  const canManageTenant = useCallback(
    (t: TenantKey) => groupAdmin || adminOf.includes(t),
    [groupAdmin, adminOf],
  );

  const tenantPath = useCallback(
    (key: string, t?: TenantKey) => menuPath(key, t ?? dataTenant, !t || t === tenant ? biz : null),
    [dataTenant, tenant, biz],
  );

  const switchBiz = useCallback(
    (t: TenantKey, b: BizKey) => {
      if (!tenants.includes(t)) return;
      const to = swapBizInPath(location.pathname, t, b) + (tenant === t && biz !== null ? location.search : "");
      if (to !== location.pathname + location.search) navigate(to);
    },
    [tenants, tenant, biz, location.pathname, location.search, navigate],
  );

  const switchSpace = useCallback(
    (next: SpaceKey, opts?: SwitchSpaceOptions) => {
      if (!spaces.includes(next)) return;
      const here = location.pathname + location.search + location.hash;
      const go = (to: string) => { if (to !== here) navigate(to); };
      if (opts?.to) { go(opts.to); return; }
      if (next === space && urlSpace === space) return;

      // D-03(★ 추천안): 법인 → 법인 전환에서 보던 메뉴가 새 법인에도 있고 그 법인 권한도 있으면
      // 같은 메뉴(주소의 법인만 교체, ?tab= 등 쿼리 유지), 아니면 그 법인 홈. 내 업무 ↔ 법인은 각 홈.
      const curKey = menuKeyOfPath(location.pathname);
      const menu = findMenu(curKey);
      const canSee = (m: MenuDef | undefined, t: TenantKey) => !m?.gate || m.gate.some((k) => hasPermIn(k, t));
      const tenantToTenant = isTenantKey(next) && urlSpace !== null && isTenantKey(urlSpace);
      if (tenantToTenant && !isHomeKey(curKey)) {
        const has = spaceHasMenu(next, curKey);
        if (has && canSee(menu, next)) {
          go(swapTenantInPath(location.pathname, next) + location.search + location.hash);
          return;
        }
        if (menu) {
          toast.info(
            has
              ? `${SPACE_META[next].label}의 '${menu.label}' 메뉴를 볼 권한이 없어 홈으로 이동했습니다.`
              : `${SPACE_META[next].label}에는 '${menu.label}' 메뉴가 없어 홈으로 이동했습니다.`,
          );
        }
      }
      go(spaceHomePath(next));
    },
    [spaces, space, urlSpace, location.pathname, location.search, location.hash, hasPermIn, navigate, toast],
  );

  const value = useMemo<SpaceContextValue>(
    () => ({
      space, tenant, biz, dataTenant, defaultSpace, spaces, tenants, myBizs, membership, membershipLoaded, canManageTenant,
      mockMode, hasPermIn, tenantPath, switchSpace, switchBiz, sidebarTint, setSidebarTint,
    }),
    [space, tenant, biz, dataTenant, defaultSpace, spaces, tenants, myBizs, membership, membershipLoaded, canManageTenant,
      mockMode, hasPermIn, tenantPath, switchSpace, switchBiz, sidebarTint, setSidebarTint],
  );

  // 소속 확인 전에는 그리지 않는다 — TenantGate 가 빈 소속으로 판정해 기본 공간으로 튕기는 것 방지(전체 admin 은 즉시)
  return <SpaceContext.Provider value={value}>{membershipLoaded ? children : null}</SpaceContext.Provider>;
}
